import React, { useEffect, useMemo, useState } from 'react';
import { fetchMenuTree, createProduct, createVariant, deleteVariant, updateProduct, deleteProduct, updateVariant } from '../api/productsApi';
import { apiFetch } from '../api/client';
import './MenuManagement.css';

// Gestione menu: visualizza categorie -> sottocategorie -> prodotti -> varianti
// con ricerca per nome, filtro categoria/sottocategoria e creazione rapida prodotto.
export default function MenuManagement() {
    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [newProduct, setNewProduct] = useState({
        name: '',
        description: '',
        allergeni: '',
        price: '',
        categoryId: '',
        subcategoryId: ''
    });
    const [variantDrafts, setVariantDrafts] = useState({});
    const [editingVariantByProduct, setEditingVariantByProduct] = useState({});
    const [editingProductId, setEditingProductId] = useState(null);
    const [productEdits, setProductEdits] = useState({});

    // Filtri (UI only, lato client)
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [subcategoryFilter, setSubcategoryFilter] = useState('');

    const categories = useMemo(() => tree.map(n => n.category), [tree]);
    const subcategoriesByCategory = useMemo(() => {
        const map = new Map();
        tree.forEach(n => { map.set(n.category.id, (n.subcategories || []).map(sn => sn.subcategory)); });
        return map;
    }, [tree]);

    async function load() {
        setLoading(true); setError(null);
        try {
            const data = await fetchMenuTree();
            if (!Array.isArray(data)) throw new Error('Snapshot tree ricostruito non è un array');
            setTree(data);
            setVariantDrafts({});
            setEditingVariantByProduct({});
        } catch (e) {
            setError(e.message || 'Errore caricamento');
        } finally { setLoading(false); }
    }

    useEffect(() => { load(); }, []);

    async function handleCreateProduct(e) {
        e.preventDefault();
        try {
            const payload = {
                name: newProduct.name,
                description: newProduct.description,
                allergeni: newProduct.allergeni,
                price: parseFloat(newProduct.price || '0'),
                ...(newProduct.subcategoryId
                    ? { subcategory: { id: Number(newProduct.subcategoryId) } }
                    : { category: { id: Number(newProduct.categoryId) } })
            };
            await createProduct(payload);
            setNewProduct({ name: '', description: '', allergeni: '', price: '', categoryId: '', subcategoryId: '' });
            load();
        } catch (err) {
            alert('Errore creazione prodotto: ' + err.message);
        }
    }

    function beginEdit(product) {
        setEditingProductId(product.id);
        setProductEdits(prev => ({
            ...prev,
            [product.id]: {
                name: product.name || '',
                description: product.description || '',
                price: product.price,
                allergeni: product.allergeni || ''
            }
        }));
    }

    function cancelEdit(id) {
        setEditingProductId(null);
    }

    async function saveEdit(id) {
        const edits = productEdits[id];
        if (!edits) return;
        try {
            await updateProduct(id, {
                name: edits.name,
                description: edits.description,
                price: parseFloat(edits.price),
                allergeni: edits.allergeni
            });
            setEditingProductId(null);
            load();
        } catch (e) {
            alert('Errore salvataggio: ' + e.message);
        }
    }

    async function removeProduct(id) {
        if (!window.confirm('Eliminare prodotto?')) return;
        try { await deleteProduct(id); load(); } catch (e) { alert('Errore eliminazione: ' + e.message); }
    }

    async function handleCreateVariant(productId) {
        const draft = variantDrafts[productId];
        if (!draft || !draft.name) return;
        try {
            const variantId = editingVariantByProduct[productId];
            if (variantId) {
                await updateVariant(Number(variantId), { name: draft.name, price: parseFloat(draft.price || '0'), available: true });
                setEditingVariantByProduct(prev => ({ ...prev, [productId]: null }));
            } else {
                await createVariant(Number(productId), { name: draft.name, price: parseFloat(draft.price || '0'), available: true, position: 0 });
            }
            setVariantDrafts(v => ({ ...v, [productId]: { name: '', price: '' } }));
            await load();
        } catch (e) { alert('Errore creazione variante: ' + e.message); }
    }

    async function handleDeleteVariant(id) {
        if (!window.confirm('Eliminare variante?')) return;
        try { await deleteVariant(id); await load(); } catch (e) { alert(e.message); }
    }

    function beginEditVariant(variant, productId) {
        setVariantDrafts(v => ({ ...v, [productId]: { name: variant.name || '', price: String(variant.price ?? '') } }));
        setEditingVariantByProduct(prev => ({ ...prev, [productId]: variant.id }));
    }

    function cancelVariantEdit(productId) {
        setVariantDrafts(v => ({ ...v, [productId]: { name: '', price: '' } }));
        setEditingVariantByProduct(prev => ({ ...prev, [productId]: null }));
    }

    async function createCategoryQuick() {
        const name = prompt('Nome categoria');
        if (!name) return;
        try {
            await apiFetch('/api/category', { method: 'POST', body: JSON.stringify({ name }) });
            load();
        } catch (e) { alert('Errore categoria: ' + e.message); }
    }

    async function createSubcategoryQuick(catId) {
        const name = prompt('Nome sottocategoria');
        if (!name) return;
        try {
            await apiFetch('/api/subcategories', { method: 'POST', body: JSON.stringify({ name, category: { id: catId } }) });
            load();
        } catch (e) { alert('Errore sottocategoria: ' + e.message); }
    }

    // ---- Filtri client-side ----
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const matchesSearch = (item) => {
        if (!normalizedSearch) return true;
        const name = (item.name || '').toLowerCase();
        const desc = (item.description || '').toLowerCase();
        return name.includes(normalizedSearch) || desc.includes(normalizedSearch);
    };

    const filteredTree = useMemo(() => {
        return tree
            .filter(node => !categoryFilter || String(node.category.id) === String(categoryFilter))
            .map(node => {
                const items = (node.items || []).filter(matchesSearch);
                const filteredSubs = (node.subcategories || [])
                    .filter(sn => !subcategoryFilter || String(sn.subcategory.id) === String(subcategoryFilter))
                    .map(sn => ({
                        ...sn,
                        items: (sn.items || []).filter(matchesSearch)
                    }));
                const directItems = subcategoryFilter ? [] : items;
                return { ...node, items: directItems, subcategories: filteredSubs };
            })
            .filter(node => {
                if (!normalizedSearch && !subcategoryFilter) return true;
                const hasDirect = (node.items || []).length > 0;
                const hasInSubs = (node.subcategories || []).some(sn => (sn.items || []).length > 0);
                return hasDirect || hasInSubs;
            });
    }, [tree, normalizedSearch, categoryFilter, subcategoryFilter]);

    useEffect(() => {
        if (!categoryFilter) {
            setSubcategoryFilter('');
            return;
        }
        const subs = subcategoriesByCategory.get(Number(categoryFilter)) || [];
        if (subcategoryFilter && !subs.some(s => String(s.id) === String(subcategoryFilter))) {
            setSubcategoryFilter('');
        }
    }, [categoryFilter, subcategoriesByCategory, subcategoryFilter]);

    const visibleProductsCount = useMemo(() => {
        let count = 0;
        filteredTree.forEach(node => {
            count += (node.items || []).length;
            (node.subcategories || []).forEach(sn => { count += (sn.items || []).length; });
        });
        return count;
    }, [filteredTree]);

    const subcategoryOptions = categoryFilter
        ? (subcategoriesByCategory.get(Number(categoryFilter)) || [])
        : [];

    // ---- Render helpers ----
    const renderEditPanel = (item) => (
        <div className="mm-edit">
            <input
                className="mm-input"
                value={productEdits[item.id]?.name || ''}
                onChange={e => setProductEdits(p => ({ ...p, [item.id]: { ...p[item.id], name: e.target.value } }))}
                placeholder="Nome"
            />
            <textarea
                className="mm-input mm-textarea"
                value={productEdits[item.id]?.description || ''}
                onChange={e => setProductEdits(p => ({ ...p, [item.id]: { ...p[item.id], description: e.target.value } }))}
                placeholder="Descrizione"
            />
            <input
                className="mm-input"
                type="number"
                step="0.01"
                value={productEdits[item.id]?.price || ''}
                onChange={e => setProductEdits(p => ({ ...p, [item.id]: { ...p[item.id], price: e.target.value } }))}
                placeholder="Prezzo base"
            />
            <input
                className="mm-input"
                value={productEdits[item.id]?.allergeni || ''}
                onChange={e => setProductEdits(p => ({ ...p, [item.id]: { ...p[item.id], allergeni: e.target.value } }))}
                placeholder="Allergeni"
            />
            <div className="mm-edit__row">
                <button className="mm-btn mm-btn--primary mm-btn--sm" onClick={() => saveEdit(item.id)}>Salva</button>
                <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => cancelEdit(item.id)}>Annulla</button>
            </div>
        </div>
    );

    const renderVariants = (item) => {
        const draft = variantDrafts[item.id] || { name: '', price: '' };
        const isEditingVariant = !!editingVariantByProduct[item.id];
        return (
            <div className="mm-variants">
                <div className="mm-variants__label">Varianti</div>
                {item.variants && item.variants.length > 0 ? (
                    <ul className="mm-variants__list">
                        {item.variants.map(v => (
                            <li key={v.id} className="mm-variant">
                                <span>{v.name}</span>
                                <span className="mm-variant__price">€{Number(v.price || 0).toFixed(2)}</span>
                                <button className="mm-variant__btn" title="Modifica" onClick={() => beginEditVariant(v, item.id)}>✏️</button>
                                <button className="mm-variant__btn" title="Elimina" onClick={() => handleDeleteVariant(v.id)}>✕</button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="mm-variants__empty">Nessuna variante</div>
                )}
                <div className="mm-variants__form">
                    <input
                        className="mm-input"
                        placeholder="Nome variante"
                        value={draft.name || ''}
                        onChange={e => setVariantDrafts(v => ({ ...v, [item.id]: { ...(v[item.id] || {}), name: e.target.value } }))}
                    />
                    <input
                        className="mm-input mm-input--price"
                        placeholder="Prezzo"
                        type="number"
                        step="0.01"
                        value={draft.price || ''}
                        onChange={e => setVariantDrafts(v => ({ ...v, [item.id]: { ...(v[item.id] || {}), price: e.target.value } }))}
                    />
                    <button className="mm-btn mm-btn--secondary mm-btn--sm" onClick={() => handleCreateVariant(item.id)}>
                        {isEditingVariant ? 'Salva variante' : '+ Variante'}
                    </button>
                    {isEditingVariant && (
                        <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => cancelVariantEdit(item.id)}>Annulla</button>
                    )}
                </div>
            </div>
        );
    };

    const renderProduct = (item) => (
        <li key={item.id} className="mm-product">
            <div className="mm-product__row">
                <span className="mm-product__name">{item.name}</span>
                <span className="mm-product__price">€{Number(item.price || 0).toFixed(2)}</span>
                {editingProductId !== item.id && (
                    <div className="mm-product__actions">
                        <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => beginEdit(item)}>Modifica</button>
                        <button className="mm-btn mm-btn--danger mm-btn--sm" onClick={() => removeProduct(item.id)}>Elimina</button>
                    </div>
                )}
            </div>
            {editingProductId === item.id && renderEditPanel(item)}
            {renderVariants(item)}
        </li>
    );

    return (
        <div className="mm-page">
            <header className="mm-page__header">
                <div>
                    <h2 className="mm-page__title">Gestione Menu</h2>
                    <p className="mm-page__subtitle">Crea, modifica e organizza categorie, sottocategorie, prodotti e varianti.</p>
                </div>
                <div className="mm-page__actions">
                    <button className="mm-btn" onClick={load} disabled={loading}>
                        {loading ? 'Caricamento…' : '↻ Ricarica'}
                    </button>
                    <button className="mm-btn mm-btn--primary" onClick={createCategoryQuick}>+ Categoria</button>
                </div>
            </header>

            {error && <div className="mm-error">Errore: {error}</div>}

            <div className="mm-toolbar">
                <div className="mm-search">
                    <span className="mm-search__icon">🔍</span>
                    <input
                        className="mm-search__input"
                        placeholder="Cerca prodotto per nome o descrizione…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <select
                    className="mm-select"
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                >
                    <option value="">Tutte le categorie</option>
                    {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
                <select
                    className="mm-select"
                    value={subcategoryFilter}
                    onChange={e => setSubcategoryFilter(e.target.value)}
                    disabled={!categoryFilter || subcategoryOptions.length === 0}
                >
                    <option value="">
                        {categoryFilter ? 'Tutte le sottocategorie' : 'Seleziona categoria'}
                    </option>
                    {subcategoryOptions.map(sc => (
                        <option key={sc.id} value={sc.id}>{sc.name}</option>
                    ))}
                </select>
                <div className="mm-toolbar__counter">{visibleProductsCount} prodotti</div>
            </div>

            <div className="mm-layout">
                <div>
                    {loading && tree.length === 0 && <div className="mm-loading">Caricamento menu…</div>}

                    {!loading && filteredTree.length === 0 && tree.length > 0 && (
                        <div className="mm-empty">
                            <div className="mm-empty__title">Nessun risultato</div>
                            <div>Nessun prodotto corrisponde ai filtri selezionati.</div>
                        </div>
                    )}

                    {!loading && tree.length === 0 && !error && (
                        <div className="mm-empty">
                            <div className="mm-empty__title">Menu vuoto</div>
                            <div>Aggiungi una categoria per iniziare a costruire il tuo menu.</div>
                        </div>
                    )}

                    {filteredTree.map(node => (
                        <section key={node.category.id} className="mm-category">
                            <header className="mm-category__header">
                                <h3 className="mm-category__title">{node.category.name}</h3>
                                <button
                                    className="mm-btn mm-btn--ghost mm-btn--sm"
                                    onClick={() => createSubcategoryQuick(node.category.id)}
                                >
                                    + Sottocategoria
                                </button>
                            </header>
                            <div className="mm-category__body">
                                {node.items && node.items.length > 0 && (
                                    <ul className="mm-products">
                                        {node.items.map(renderProduct)}
                                    </ul>
                                )}
                                {node.subcategories && node.subcategories.length > 0 && node.subcategories.map(sn => (
                                    <div key={sn.subcategory.id} className="mm-subcategory">
                                        <h4 className="mm-subcategory__title">{sn.subcategory.name}</h4>
                                        {sn.items && sn.items.length > 0 ? (
                                            <ul className="mm-products">
                                                {sn.items.map(renderProduct)}
                                            </ul>
                                        ) : (
                                            <div className="mm-subcategory__empty">Nessun prodotto</div>
                                        )}
                                    </div>
                                ))}
                                {(!node.items || node.items.length === 0) && (!node.subcategories || node.subcategories.length === 0) && (
                                    <div className="mm-category__empty">
                                        Nessun prodotto in questa categoria.
                                    </div>
                                )}
                            </div>
                        </section>
                    ))}
                </div>

                <aside>
                    <form className="mm-form" onSubmit={handleCreateProduct}>
                        <h3 className="mm-form__title">Nuovo Prodotto</h3>
                        <input
                            className="mm-input"
                            placeholder="Nome"
                            value={newProduct.name}
                            onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))}
                            required
                        />
                        <textarea
                            className="mm-input mm-textarea"
                            placeholder="Descrizione"
                            value={newProduct.description}
                            onChange={e => setNewProduct(p => ({ ...p, description: e.target.value }))}
                        />
                        <input
                            className="mm-input"
                            placeholder="Allergeni"
                            value={newProduct.allergeni}
                            onChange={e => setNewProduct(p => ({ ...p, allergeni: e.target.value }))}
                        />
                        <input
                            className="mm-input"
                            placeholder="Prezzo base"
                            type="number"
                            step="0.01"
                            value={newProduct.price}
                            onChange={e => setNewProduct(p => ({ ...p, price: e.target.value }))}
                            required
                        />
                        <label className="mm-form__label">Categoria</label>
                        <select
                            className="mm-select"
                            value={newProduct.categoryId}
                            onChange={e => setNewProduct(p => ({ ...p, categoryId: e.target.value, subcategoryId: '' }))}
                            required={!newProduct.subcategoryId}
                        >
                            <option value="">— seleziona categoria —</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <label className="mm-form__label">Sottocategoria (opzionale)</label>
                        <select
                            className="mm-select"
                            value={newProduct.subcategoryId}
                            onChange={e => setNewProduct(p => ({ ...p, subcategoryId: e.target.value, categoryId: p.categoryId }))}
                            disabled={!newProduct.categoryId}
                        >
                            <option value="">— nessuna —</option>
                            {(subcategoriesByCategory.get(Number(newProduct.categoryId)) || []).map(sc =>
                                <option key={sc.id} value={sc.id}>{sc.name}</option>
                            )}
                        </select>
                        <button type="submit" className="mm-btn mm-btn--primary mm-form__submit">
                            Crea Prodotto
                        </button>
                        <p className="mm-form__hint">
                            Il prodotto può appartenere direttamente alla categoria oppure a una sottocategoria.
                        </p>
                    </form>
                </aside>
            </div>
        </div>
    );
}
